// src/pages/requisiciones/RequisicionesPage.js
import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Badge, Form, Row, Col, 
  Spinner, Alert, Pagination, InputGroup 
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { listarRequisiciones } from '../../services/requisiciones/requisicionService';
import { Alertassw } from '../../components/Alertas/Alertassw';
import { useAuth } from '../../hooks/useAuth';
import { EDITAR_REQUISICION } from '../../components/Roles/ConstantRoute';

const RequisicionesPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const permisos = userData?.role?.permisos || [];
  const tienePermiso = (perm) => permisos.includes(perm);
  const puedeEditarRequisicion = tienePermiso(EDITAR_REQUISICION);

  const [loading, setLoading] = useState(false);
  const [requisiciones, setRequisiciones] = useState([]);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    estado: '',
    fechaDesde: '',
    fechaHasta: ''
  });

  const [filtrosAplicados, setFiltrosAplicados] = useState({
    estado: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  
  // Paginación
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    cargarRequisiciones(filtrosAplicados, pagination.page);
  }, [pagination.page, filtrosAplicados]);

  const cargarRequisiciones = async (filtrosActivos, page) => {
    setLoading(true);
    try {
      const params = { ...filtrosActivos, page, limit: pagination.limit };
      // Remover filtros vacíos
      Object.keys(params).forEach(key => { if (!params[key]) delete params[key]; });
      
      const response = await listarRequisiciones(params);
      
      if (response.status === 200 && response.data) {
        setRequisiciones(response.data.data || []);
        // Backend devuelve total/totalPages flat (no anidado en .pagination)
        setPagination(prev => ({
          ...prev,
          total: response.data.total ?? 0,
          totalPages: response.data.totalPages ?? 0,
        }));
      } else {
        Alertassw('error', 'Error', 'Error al cargar requisiciones');
      }
    } catch (error) {
      console.error('Error al cargar requisiciones:', error);
      Alertassw('error', 'Error', 'Error de conexión al cargar requisiciones');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const handleBuscar = () => {
    setFiltrosAplicados({ ...filtros });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleLimpiarFiltros = () => {
    const vacios = { estado: '', fechaDesde: '', fechaHasta: '' };
    setFiltros(vacios);
    setFiltrosAplicados(vacios);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCambiarPagina = (nuevaPagina) => {
    setPagination(prev => ({ ...prev, page: nuevaPagina }));
  };

  // TIN-23 P6: labels genéricos sin "Comercial"
  const ESTADO_LABEL = {
    'Borrador': 'Borrador',
    'Enviada_Comercial': 'Enviada para Aprobación',
    'Aprobada_Comercial': 'Aprobada',
    'Enviada_Proveedor': 'Enviada a Proveedor',
    'Cotizacion_Recibida': 'Cotización Recibida',
    'Orden_Compra_Emitida': 'OC Emitida',
    'Orden_Compra_Aprobada': 'OC Aprobada',
    'Orden_Enviada_Proveedor': 'OC Enviada al Proveedor',
    'Certificados_Pendientes': 'Certificados Pendientes',
    'Certificados_Recibidos': 'Certificados Recibidos',
    'Revision_Entrega': 'Revisión Entrega',
    'Devolucion': 'Devolución',
    'Rechazada': 'Rechazada',
    'Cerrada': 'Cerrada'
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      'Borrador': { bg: 'secondary', icon: 'pencil' },
      'Enviada_Comercial': { bg: 'info', icon: 'send' },
      'Aprobada_Comercial': { bg: 'success', icon: 'check-circle' },
      'Enviada_Proveedor': { bg: 'warning', icon: 'truck' },
      'Cotizacion_Recibida': { bg: 'primary', icon: 'file-earmark-text' },
      'Orden_Compra_Emitida': { bg: 'warning', icon: 'file-earmark-ruled' },
      'Orden_Compra_Aprobada': { bg: 'success', icon: 'file-earmark-check' },
      'Orden_Enviada_Proveedor': { bg: 'info', icon: 'send-check' },
      'Certificados_Pendientes': { bg: 'warning', icon: 'patch-question' },
      'Certificados_Recibidos': { bg: 'success', icon: 'patch-check' },
      'Revision_Entrega': { bg: 'warning', icon: 'search' },
      'Devolucion': { bg: 'danger', icon: 'arrow-counterclockwise' },
      'Rechazada': { bg: 'danger', icon: 'x-circle' },
      'Cerrada': { bg: 'dark', icon: 'lock' }
    };

    const est = estados[estado] || estados['Borrador'];

    return (
      <Badge bg={est.bg}>
        <i className={`bi bi-${est.icon} me-1`}></i>
        {ESTADO_LABEL[estado] || estado?.replace(/_/g, ' ')}
      </Badge>
    );
  };

  // TIN-23 P5: el botón Editar requiere el permiso EDITAR_REQUISICION
  // (asignable desde Roles → Permisos) Y que la requisición no tenga
  // ganador seleccionado ni esté en estado terminal.
  const tieneGanador = (req) =>
    (req.proveedoresAsignados || []).some(pa => pa.estadoEnvio === 'Ganador');
  const puedeEditar = (req) =>
    puedeEditarRequisicion &&
    !tieneGanador(req) &&
    !['Cerrada', 'Rechazada'].includes(req.estado);

  const generarPaginacion = () => {
    const items = [];
    const maxPaginas = 5;
    let inicio = Math.max(1, pagination.page - Math.floor(maxPaginas / 2));
    let fin = Math.min(pagination.totalPages, inicio + maxPaginas - 1);
    
    if (fin - inicio + 1 < maxPaginas) {
      inicio = Math.max(1, fin - maxPaginas + 1);
    }
    
    if (pagination.page > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => handleCambiarPagina(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => handleCambiarPagina(pagination.page - 1)} />
      );
    }
    
    for (let num = inicio; num <= fin; num++) {
      items.push(
        <Pagination.Item
          key={num}
          active={num === pagination.page}
          onClick={() => handleCambiarPagina(num)}
        >
          {num}
        </Pagination.Item>
      );
    }
    
    if (pagination.page < pagination.totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => handleCambiarPagina(pagination.page + 1)} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => handleCambiarPagina(pagination.totalPages)} />
      );
    }
    
    return items;
  };

  return (
    <div className="container-fluid py-4">
      <Card className='w-100'>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center w-100">
          <h5 className="mb-0">
            <i className="bi bi-clipboard-check me-2"></i>
            Requisiciones
          </h5>
          <Button 
            variant="light" 
            size="sm"
            onClick={() => navigate('/requisiciones/nueva')}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Nueva Requisición
          </Button>
        </Card.Header>
        
        <Card.Body className='w-100'>
          {/* FILTROS */}
          <Card className="mb-3 border-0 shadow-sm w-100">
            <Card.Body className="bg-light w-100">
              <Row className="g-3">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Estado</Form.Label>
                    <Form.Select
                      name="estado"
                      value={filtros.estado}
                      onChange={handleFiltroChange}
                      size="sm"
                    >
                      <option value="">Todos los estados</option>
                      <option value="Borrador">Borrador</option>
                      <option value="Enviada_Comercial">Enviada para Aprobación</option>
                      <option value="Aprobada_Comercial">Aprobada</option>
                      <option value="Enviada_Proveedor">Enviada a Proveedor</option>
                      <option value="Cotizacion_Recibida">Cotización Recibida</option>
                      <option value="Orden_Compra_Emitida">OC Emitida</option>
                      <option value="Certificados_Recibidos">Certificados Recibidos</option>
                      <option value="Revision_Entrega">Revisión Entrega</option>
                      <option value="Devolucion">Devolución</option>
                      <option value="Rechazada">Rechazada</option>
                      <option value="Cerrada">Cerrada</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Fecha Desde</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaDesde"
                      value={filtros.fechaDesde}
                      onChange={handleFiltroChange}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Fecha Hasta</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaHasta"
                      value={filtros.fechaHasta}
                      onChange={handleFiltroChange}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                
                <Col xs={12} md={2} className="d-flex align-items-end gap-2">
                  <Button 
                    variant="primary" 
                    size="sm" 
                    className="w-100"
                    onClick={handleBuscar}
                    disabled={loading}
                  >
                    <i className="bi bi-search me-1"></i>
                    Buscar
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={handleLimpiarFiltros}
                    disabled={loading}
                  >
                    <i className="bi bi-x-circle"></i>
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* TABLA */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Cargando requisiciones...</p>
            </div>
          ) : requisiciones.length === 0 ? (
            <Alert variant="info" className="text-center">
              <i className="bi bi-info-circle me-2"></i>
              No se encontraron requisiciones
            </Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Consecutivo</th>
                      <th>Título</th>
                      <th>Estado</th>
                      <th className="d-none d-lg-table-cell">Creado Por</th>
                      <th className="d-none d-lg-table-cell">Fecha Creación</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requisiciones.map((req) => (
                      <tr 
                        key={req._id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/requisiciones/${req._id}`)}
                      >
                        <td className="fw-bold">{req.consecutivo}</td>
                        <td>{req.titulo}</td>
                        <td>{getEstadoBadge(req.estado)}</td>
                        <td className="d-none d-lg-table-cell">
                          {req.creadaPor ? `${req.creadaPor.nombre} ${req.creadaPor.apellido}` : '-'}
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {moment(req.fechaCreacion).format('DD/MM/YYYY HH:mm')}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => navigate(`/requisiciones/${req._id}`)}
                              title="Ver detalle"
                            >
                              <i className="bi bi-eye"></i>
                            </Button>
                            {/* TIN-23 P5: editar si no hay ganador ni está cerrada/rechazada */}
                            {puedeEditar(req) && (
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => navigate(`/requisiciones/editar/${req._id}`)}
                                title="Editar requisición"
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* PAGINACIÓN */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Mostrando {requisiciones.length} de {pagination.total} requisiciones
                </small>
                {pagination.totalPages > 1 && (
                  <Pagination size="sm" className="mb-0">
                    {generarPaginacion()}
                  </Pagination>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RequisicionesPage;
